import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import ListPagination from '../../../components/Office/ListPagination';
import './PaymentsList.css';

const PAGE_SIZE = 20;

const PaymentsList = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tradeLicenseApi.getPayments({
        ...(filter ? { status: filter } : {}),
        page,
        page_size: PAGE_SIZE,
      });
      const data = response.data;
      setPayments(data.results || data);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setPayments([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleVerifyPayment = async () => {
    if (!selectedPayment) return;
    setActionLoading(true);
    try {
      await tradeLicenseApi.verifyPayment(selectedPayment.id);
      alert('Payment verified successfully!');
      setSelectedPayment(null);
      fetchPayments();
    } catch (error) {
      alert('Error verifying payment: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="payments-container">
      <h1 className="page-title">Payment Management</h1>

      <div className="filter-bar">
        <button
          className={`btn ${!filter ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter(''); setPage(1); }}
        >
          All
        </button>
        <button
          className={`btn ${filter === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter('pending'); setPage(1); }}
        >
          Pending Verification
        </button>
        <button
          className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter('completed'); setPage(1); }}
        >
          Completed
        </button>
        <button
          className={`btn ${filter === 'failed' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setFilter('failed'); setPage(1); }}
        >
          Failed
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : payments.length > 0 ? (
        <>
        <div className="payments-table">
          <table>
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Application</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>
                    <strong>{payment.payment_id}</strong>
                  </td>
                  <td>
                    <Link to={`/office/applications/${payment.application}`}>
                      {payment.application_number}
                    </Link>
                  </td>
                  <td className="amount">₹{Number(payment.amount).toFixed(2)}</td>
                  <td>{payment.payment_method?.replace(/_/g, ' ')}</td>
                  <td>
                    <span className={`status-badge status-${payment.status}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td>{payment.paid_date ? new Date(payment.paid_date).toLocaleDateString() : '—'}</td>
                  <td>
                    <button
                      className="btn btn-primary btn-small"
                      onClick={() => setSelectedPayment(payment)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ListPagination
          count={pagination.count}
          next={pagination.next}
          previous={pagination.previous}
          currentPage={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
        />
        </>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <p>No payments found</p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedPayment && (
        <div className="modal-overlay" onClick={() => setSelectedPayment(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Payment Details</h2>

            <dl className="detail-list">
              <dt>Payment ID:</dt>
              <dd>{selectedPayment.payment_id}</dd>

              <dt>Application:</dt>
              <dd>{selectedPayment.application_number}</dd>

              <dt>Amount:</dt>
              <dd className="amount">₹{Number(selectedPayment.amount).toFixed(2)}</dd>

              <dt>Payment Method:</dt>
              <dd>{selectedPayment.payment_method}</dd>

              <dt>Status:</dt>
              <dd>
                <span className={`status-badge status-${selectedPayment.status}`}>
                  {selectedPayment.status}
                </span>
              </dd>

              <dt>Paid By:</dt>
              <dd>{selectedPayment.paid_by_name || 'N/A'}</dd>

              <dt>Reference:</dt>
              <dd>{selectedPayment.gateway_transaction_id || selectedPayment.gateway_reference || 'N/A'}</dd>

              <dt>Remarks:</dt>
              <dd>{selectedPayment.remarks || 'N/A'}</dd>

              <dt>Receipt Number:</dt>
              <dd>{selectedPayment.receipt_number || 'Not generated'}</dd>

              <dt>Paid Date:</dt>
              <dd>{selectedPayment.paid_date ? new Date(selectedPayment.paid_date).toLocaleDateString() : 'N/A'}</dd>

              <dt>Verified By:</dt>
              <dd>{selectedPayment.verified_by_name || 'N/A'}</dd>
            </dl>

            {selectedPayment.status === 'pending' && (
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedPayment(null)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleVerifyPayment}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Verifying...' : 'Verify Payment'}
                </button>
              </div>
            )}

            {selectedPayment.status !== 'pending' && (
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setSelectedPayment(null)}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsList;